import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import LeadCaptureBox from "@/components/LeadCaptureBox";
import { SITE_URL } from "@/lib/siteConfig";
import {
  ArrowRight, TrendingUp, Users, BarChart3, Target,
  Zap, Calendar, ChevronDown, ChevronUp, ArrowDown,
  Minus, Plus, Calculator,
} from "lucide-react";

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

interface Projection {
  month: number;
  label: string;
  subscribers: number;
  newAdded: number;
  churned: number;
  netGrowth: number;
}

function buildProjections(
  currentSize: number,
  monthlyNewSubs: number,
  monthlyChurnPct: number,
  months: number,
): Projection[] {
  const projections: Projection[] = [];
  let subs = currentSize;

  for (let m = 0; m <= months; m++) {
    if (m === 0) {
      projections.push({
        month: 0, label: "Now", subscribers: subs,
        newAdded: 0, churned: 0, netGrowth: 0,
      });
      continue;
    }

    const churned = Math.round(subs * (monthlyChurnPct / 100));
    const newSubs = monthlyNewSubs;
    const net = newSubs - churned;
    subs = Math.max(0, subs + net);

    projections.push({
      month: m,
      label: `Month ${m}`,
      subscribers: subs,
      newAdded: newSubs,
      churned,
      netGrowth: net,
    });
  }

  return projections;
}

function SliderField({ label, id, min, max, step, value, displayVal, onChange, hint }: {
  label: string; id: string; min: number; max: number; step?: number;
  value: number; displayVal: string; onChange: (v: number) => void; hint?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label htmlFor={id} className="text-xs font-semibold text-ink">{label}</label>
        <span className="text-sm font-bold text-coral font-mono">{displayVal}</span>
      </div>
      {hint && <p className="text-[10px] text-slate mb-2">{hint}</p>}
      <input id={id} type="range" min={min} max={max} step={step || 1} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full bg-sand accent-coral cursor-pointer" />
      <div className="flex justify-between text-[10px] text-slate/50 mt-1">
        <span>{min.toLocaleString()}</span>
        <span>{max.toLocaleString()}</span>
      </div>
    </div>
  );
}

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "Email List Growth Calculator",
  applicationCategory: "BusinessApplication",
  description: "Project your email list growth over 3, 6, and 12 months. See the impact of reducing churn and increasing signups with interactive visualizations.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
  operatingSystem: "Web",
};

export default function ListGrowthCalculatorPage() {
  const [currentSize, setCurrentSize] = useState(2500);
  const [monthlyNewSubs, setMonthlyNewSubs] = useState(300);
  const [churnRate, setChurnRate] = useState(2.5);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const projections = useMemo(
    () => buildProjections(currentSize, monthlyNewSubs, churnRate, 12),
    [currentSize, monthlyNewSubs, churnRate]
  );

  // Improved scenario: 50% more signups, 1% less churn
  const improvedProjections = useMemo(
    () => buildProjections(currentSize, Math.round(monthlyNewSubs * 1.5), Math.max(0, churnRate - 1), 12),
    [currentSize, monthlyNewSubs, churnRate]
  );

  const month3 = projections[3];
  const month6 = projections[6];
  const month12 = projections[12];
  const improved12 = improvedProjections[12];

  const totalChurned12 = projections.slice(1).reduce((sum, p) => sum + p.churned, 0);
  const totalAdded12 = projections.slice(1).reduce((sum, p) => sum + p.newAdded, 0);
  const netGrowth12 = month12.subscribers - currentSize;
  const growthPct = currentSize > 0 ? Math.round((netGrowth12 / currentSize) * 100) : 0;

  // Chart bar heights
  const maxSubs = Math.max(...projections.map(p => p.subscribers), 1);

  return (
    <>
      <SeoHead
        title="Email List Growth Calculator — Project Your Subscriber Growth | MoreMail"
        description="Free email list growth calculator. Enter your current list size, monthly signups, and churn rate. See projected growth over 3, 6, and 12 months with interactive charts."
        canonical={`${SITE_URL}/list-growth-calculator`}
        keywords="email list growth calculator, subscriber growth calculator, email list projections, churn rate calculator, email marketing growth, list growth forecasting, subscriber forecast tool"
        ogTitle="Email List Growth Calculator — See Your Future List Size | MoreMail"
        ogDescription="Enter your numbers. See your projected list size over 12 months. Visualize the impact of reducing churn and growing signups."
        jsonLd={JSON_LD}
      />

      {/* Hero */}
      <section className="pt-12 pb-6 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }} />
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-5 backdrop-blur-sm">
            <TrendingUp size={13} className="text-coral" /> Free Tool
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-white leading-tight mb-3">
            Email List Growth <span className="text-coral">Calculator</span>
          </h1>
          <p className="text-white/50 text-base max-w-lg mx-auto">
            See where your email list is headed. Project growth over 12 months
            and see the impact of reducing churn.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-10 bg-paper">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-6">

            {/* Inputs */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white rounded-xl border border-sand p-5">
                <h2 className="font-display font-bold text-ink text-sm mb-4 flex items-center gap-2">
                  <Users size={15} className="text-coral" /> Your Numbers
                </h2>
                <div className="space-y-5">
                  <SliderField label="Current List Size" id="currentSize" min={0} max={500000} step={100}
                    value={currentSize} displayVal={fmtNum(currentSize)} onChange={setCurrentSize}
                    hint="Active subscribers today" />

                  <SliderField label="New Subscribers / Month" id="newSubs" min={0} max={50000} step={10}
                    value={monthlyNewSubs} displayVal={fmtNum(monthlyNewSubs)} onChange={setMonthlyNewSubs}
                    hint="From signups, lead magnets, ads, etc." />

                  <SliderField label="Monthly Churn Rate" id="churn" min={0} max={15} step={0.1}
                    value={churnRate} displayVal={`${churnRate.toFixed(1)}%`} onChange={setChurnRate}
                    hint="Unsubscribes + bounces per month (industry avg: 2-3%)" />
                </div>
              </div>

              {/* Key insight card */}
              <div className={`rounded-xl border p-5 ${netGrowth12 >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                <h3 className="text-xs font-bold text-ink mb-2 flex items-center gap-2">
                  {netGrowth12 >= 0 ? <TrendingUp size={14} className="text-emerald-600" /> : <ArrowDown size={14} className="text-red-500" />}
                  12-Month Outlook
                </h3>
                <p className={`font-display font-bold text-2xl ${netGrowth12 >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                  {netGrowth12 >= 0 ? "+" : ""}{fmtNum(netGrowth12)} subscribers
                </p>
                <p className="text-xs text-slate mt-1">
                  {netGrowth12 >= 0
                    ? `Your list will grow ${growthPct}% over the next year.`
                    : `Your list will shrink ${Math.abs(growthPct)}% — churn exceeds new signups.`}
                </p>
                {netGrowth12 < 0 && (
                  <p className="text-xs text-red-600 mt-2 font-semibold">
                    You need at least {Math.ceil(currentSize * (churnRate / 100))} new subscribers/month to offset churn.
                  </p>
                )}
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-3 space-y-4">

              {/* Milestone cards */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: "3 Months", data: month3 },
                  { label: "6 Months", data: month6 },
                  { label: "12 Months", data: month12 },
                ].map((m) => (
                  <div key={m.label} className="bg-white rounded-xl border border-sand p-4 text-center">
                    <p className="text-[10px] text-slate font-semibold uppercase tracking-wide mb-1">{m.label}</p>
                    <p className="font-display font-bold text-xl text-ink">{fmtNum(m.data.subscribers)}</p>
                    <p className={`text-xs font-semibold mt-0.5 ${m.data.subscribers >= currentSize ? "text-emerald-600" : "text-red-500"}`}>
                      {m.data.subscribers >= currentSize ? "+" : ""}{fmtNum(m.data.subscribers - currentSize)}
                    </p>
                  </div>
                ))}
              </div>

              {/* Visual bar chart */}
              <div className="bg-white rounded-xl border border-sand p-5">
                <h3 className="font-display font-bold text-ink text-sm flex items-center gap-2 mb-4">
                  <BarChart3 size={15} className="text-coral" /> Growth Trajectory
                </h3>
                <div className="flex items-end gap-1 h-40">
                  {projections.map((p, i) => {
                    const height = maxSubs > 0 ? Math.max(4, (p.subscribers / maxSubs) * 100) : 4;
                    const isNow = i === 0;
                    const isMilestone = i === 3 || i === 6 || i === 12;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                        {/* Tooltip */}
                        <div className="absolute -top-8 hidden group-hover:block bg-ink text-white text-[10px] px-2 py-1 rounded whitespace-nowrap z-10">
                          {p.label}: {fmtNum(p.subscribers)}
                        </div>
                        <div
                          className={`w-full rounded-t transition-all duration-300 ${
                            isNow ? "bg-slate/30" :
                            p.subscribers >= currentSize ? "bg-coral" : "bg-red-400"
                          } ${isMilestone ? "opacity-100" : "opacity-70"}`}
                          style={{ height: `${height}%` }}
                        />
                        <span className="text-[8px] text-slate mt-1">
                          {isNow ? "Now" : i % 3 === 0 ? `${i}m` : ""}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Yearly summary */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded-xl border border-sand p-4 text-center">
                  <Plus size={16} className="mx-auto text-emerald-500 mb-1" />
                  <p className="font-display font-bold text-ink text-lg">{fmtNum(totalAdded12)}</p>
                  <p className="text-[10px] text-slate">Total Added (12mo)</p>
                </div>
                <div className="bg-white rounded-xl border border-sand p-4 text-center">
                  <Minus size={16} className="mx-auto text-red-400 mb-1" />
                  <p className="font-display font-bold text-ink text-lg">{fmtNum(totalChurned12)}</p>
                  <p className="text-[10px] text-slate">Total Churned (12mo)</p>
                </div>
                <div className="bg-white rounded-xl border border-sand p-4 text-center">
                  <Target size={16} className="mx-auto text-coral mb-1" />
                  <p className="font-display font-bold text-ink text-lg">{fmtNum(month12.subscribers)}</p>
                  <p className="text-[10px] text-slate">Final List Size</p>
                </div>
              </div>

              {/* Optimization scenario */}
              <div className="bg-gradient-to-r from-teal/10 to-teal-light rounded-xl border border-teal/15 p-5">
                <h3 className="font-display font-bold text-ink text-sm flex items-center gap-2 mb-2">
                  <Zap size={15} className="text-teal" /> What If You Optimized?
                </h3>
                <p className="text-xs text-slate mb-3">
                  If you increased signups by 50% and reduced churn by 1% — both achievable with better landing pages and re-engagement automations:
                </p>
                <div className="flex items-center gap-4">
                  <div className="bg-white rounded-lg px-4 py-2 border border-teal/20 text-center">
                    <p className="font-display font-bold text-teal text-xl">{fmtNum(improved12.subscribers)}</p>
                    <p className="text-[10px] text-slate">Optimized 12mo size</p>
                  </div>
                  <div className="bg-white rounded-lg px-4 py-2 border border-teal/20 text-center">
                    <p className="font-display font-bold text-teal text-xl">
                      +{fmtNum(improved12.subscribers - month12.subscribers)}
                    </p>
                    <p className="text-[10px] text-slate">Extra subscribers</p>
                  </div>
                  <div className="bg-white rounded-lg px-4 py-2 border border-teal/20 text-center">
                    <p className="font-display font-bold text-teal text-xl">
                      {currentSize > 0 ? Math.round(((improved12.subscribers - currentSize) / currentSize) * 100) : 0}%
                    </p>
                    <p className="text-[10px] text-slate">Total growth</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tips section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-2xl font-bold text-ink text-center mb-8">
            How to Grow Your List <span className="text-coral">Faster</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { title: "Increase Signups", desc: "Add lead magnets to your site, run giveaways, use exit-intent popups, and create content upgrades for your best blog posts.", icon: TrendingUp },
              { title: "Reduce Churn", desc: "Send relevant content, segment your audience, use re-engagement automations, and make sure your emails provide real value.", icon: Users },
              { title: "Clean Regularly", desc: "Remove invalid addresses, sunset inactive subscribers, and keep your bounce rate under 2% to protect sender reputation.", icon: Target },
            ].map(item => (
              <div key={item.title} className="bg-paper rounded-xl p-5 border border-sand text-center">
                <item.icon size={22} className="mx-auto text-coral mb-3" />
                <h3 className="font-display font-bold text-ink text-sm mb-1.5">{item.title}</h3>
                <p className="text-xs text-slate leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl font-bold text-white mb-3">
            Grow Your List on Autopilot
          </h2>
          <p className="text-white/50 text-base mb-8 leading-relaxed">
            MoreMail gives you pre-built automations, AI campaigns, and deliverability tools
            to grow your list and keep subscribers engaged.
          </p>
          <div className="flex justify-center mb-6">
            <LeadCaptureBox />
          </div>
          <p className="text-white/30 text-xs">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
